import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, Pencil, Package as PackageIcon, ShoppingCart, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDataCache } from '@/contexts/DataCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MercadoLibre = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { products, refreshProducts } = useDataCache();

  const [packages, setPackages] = useState([]);
  const [cart, setCart] = useState([]); // [{ml_package_id, package_qty}]
  const [loading, setLoading] = useState(true);
  const [pkgDialog, setPkgDialog] = useState({ open: false, editing: null, name: '', components: [], search: '' });
  const [submitDialog, setSubmitDialog] = useState({ open: false, pin: '', notes: '', submitting: false });
  const [history, setHistory] = useState([]);
  const [reconcileDialog, setReconcileDialog] = useState({ open: false, exit: null, password: '', submitting: false });

  const fetchPackages = async () => {
    try {
      const { data } = await axios.get(`${API}/ml-packages`, { withCredentials: true });
      setPackages(data);
    } catch (e) {
      toast.error('Error al cargar paquetes');
    } finally {
      setLoading(false);
    }
  };
  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/ml/exits`, { withCredentials: true });
      setHistory(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { fetchPackages(); fetchHistory(); }, []);

  const submitReconcile = async () => {
    if (!reconcileDialog.password.trim()) return;
    setReconcileDialog(s => ({ ...s, submitting: true }));
    try {
      await axios.post(`${API}/ml/exits/${reconcileDialog.exit.id}/reconcile`, { password: reconcileDialog.password }, { withCredentials: true });
      toast.success(`✓ ${reconcileDialog.exit.folio} cuadrada`);
      setReconcileDialog({ open: false, exit: null, password: '', submitting: false });
      fetchHistory();
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) toast.error('🔒 Contraseña incorrecta');
      else toast.error(e.response?.data?.detail || 'Error');
      setReconcileDialog(s => ({ ...s, password: '', submitting: false }));
    }
  };

  // Productos vendibles para el form de paquete (sin variantes padres, con stock manejable)
  const sellableProducts = useMemo(() => {
    return (products || []).filter(p => !p.has_variants).map(p => {
      let label = p.name || '';
      if (p.parent_product_id) {
        const parent = (products || []).find(x => x.id === p.parent_product_id);
        if (parent) label = `${parent.name} — ${p.variant_name || ''}`;
      }
      return { id: p.id, label, stock: p.stock };
    });
  }, [products]);

  const addToCart = (pkg) => {
    setCart((c) => {
      const existing = c.find(x => x.ml_package_id === pkg.id);
      if (existing) {
        return c.map(x => x.ml_package_id === pkg.id ? { ...x, package_qty: x.package_qty + 1 } : x);
      }
      return [...c, { ml_package_id: pkg.id, name: pkg.name, components: pkg.components, package_qty: 1 }];
    });
  };

  const updateCartQty = (id, qty) => {
    const n = parseInt(qty) || 0;
    if (n <= 0) {
      setCart(c => c.filter(x => x.ml_package_id !== id));
    } else {
      setCart(c => c.map(x => x.ml_package_id === id ? { ...x, package_qty: n } : x));
    }
  };

  // Totales por producto (consolidado)
  const productTotals = useMemo(() => {
    const map = {};
    for (const line of cart) {
      for (const c of (line.components || [])) {
        const k = c.product_id;
        const total = (map[k]?.total || 0) + (c.qty * line.package_qty);
        map[k] = { product_id: k, label: c.product_label || k, total, stock: c.product_stock };
      }
    }
    return Object.values(map).sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [cart]);

  const totalPackages = cart.reduce((s, l) => s + l.package_qty, 0);
  const totalPieces = productTotals.reduce((s, p) => s + p.total, 0);
  const hasInsufficientStock = productTotals.some(p => (p.stock != null) && p.total > p.stock);

  const submitExit = async () => {
    if (cart.length === 0) { toast.error('Carrito vacío'); return; }
    if (!submitDialog.pin.trim()) { toast.error('Ingresa el PIN'); return; }
    setSubmitDialog(s => ({ ...s, submitting: true }));
    try {
      const payload = {
        lines: cart.map(l => ({ ml_package_id: l.ml_package_id, package_qty: l.package_qty })),
        pin: submitDialog.pin.trim(),
        notes: submitDialog.notes.trim() || null,
      };
      const { data } = await axios.post(`${API}/ml/exit`, payload, { withCredentials: true });
      toast.success(`Salida ${data.folio} registrada · ${totalPackages} paquete(s) · ${totalPieces} pzas`);
      setCart([]);
      setSubmitDialog({ open: false, pin: '', notes: '', submitting: false });
      refreshProducts();
      fetchHistory();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error';
      if (e.response?.status === 401) {
        toast.error('🔒 PIN incorrecto');
      } else {
        toast.error(msg);
      }
      setSubmitDialog(s => ({ ...s, submitting: false }));
    }
  };

  // ----- Package CRUD -----
  const openCreatePkg = () => setPkgDialog({ open: true, editing: null, name: '', components: [], search: '' });
  const openEditPkg = (pkg) => setPkgDialog({
    open: true,
    editing: pkg,
    name: pkg.name,
    components: (pkg.components || []).map(c => ({ product_id: c.product_id, qty: c.qty, label: c.product_label, stock: c.product_stock })),
    search: ''
  });
  const addProductToPkg = (prod) => {
    setPkgDialog(d => {
      const existing = d.components.find(c => c.product_id === prod.id);
      if (existing) {
        return {
          ...d,
          components: d.components.map(c =>
            c.product_id === prod.id ? { ...c, qty: c.qty + 1 } : c
          )
        };
      }
      return {
        ...d,
        components: [...d.components, { product_id: prod.id, qty: 1, label: prod.label, stock: prod.stock }]
      };
    });
  };
  const setPkgComponentQty = (productId, qty) => {
    const n = parseInt(qty) || 0;
    setPkgDialog(d => ({
      ...d,
      components: n <= 0
        ? d.components.filter(c => c.product_id !== productId)
        : d.components.map(c => c.product_id === productId ? { ...c, qty: n } : c)
    }));
  };
  const removePkgComponent = (productId) => setPkgComponentQty(productId, 0);

  const savePkg = async () => {
    const name = pkgDialog.name.trim();
    if (!name) { toast.error('Nombre obligatorio'); return; }
    const comps = (pkgDialog.components || []).filter(c => c.product_id && c.qty > 0);
    if (comps.length === 0) { toast.error('Agrega al menos un producto'); return; }
    const payload = { name, components: comps.map(c => ({ product_id: c.product_id, qty: parseInt(c.qty) })) };
    try {
      if (pkgDialog.editing) {
        await axios.put(`${API}/ml-packages/${pkgDialog.editing.id}`, payload, { withCredentials: true });
        toast.success('Paquete actualizado');
      } else {
        await axios.post(`${API}/ml-packages`, payload, { withCredentials: true });
        toast.success('Paquete creado');
      }
      setPkgDialog({ open: false, editing: null, name: '', components: [], search: '' });
      fetchPackages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };
  const deletePkg = async (pkg) => {
    if (!window.confirm(`¿Eliminar "${pkg.name}"?`)) return;
    try {
      await axios.delete(`${API}/ml-packages/${pkg.id}`, { withCredentials: true });
      toast.success('Eliminado');
      fetchPackages();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <PackageIcon size={32} weight="bold" className="text-yellow-600" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">Mercado Libre</h1>
              <p className="text-muted-foreground">Salida de paquetes con autorización por PIN</p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={openCreatePkg} data-testid="ml-add-package-btn">
              <Plus size={18} className="mr-1" /> Nuevo paquete
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de paquetes — click para agregar */}
          <div className="lg:col-span-2 bg-white border border-border rounded-sm">
            <div className="p-4 border-b">
              <h2 className="font-semibold">Paquetes disponibles ({packages.length})</h2>
              <p className="text-xs text-muted-foreground">Haz clic para agregar al carrito.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
              {loading && <p className="col-span-2 text-center text-muted-foreground">Cargando…</p>}
              {!loading && packages.length === 0 && (
                <p className="col-span-2 text-center text-muted-foreground">
                  {isAdmin ? 'Sin paquetes. Crea uno con el botón de arriba.' : 'Pide al admin que cree paquetes.'}
                </p>
              )}
              {packages.map((pkg) => (
                <div key={pkg.id} className="border rounded-sm p-3 hover:border-yellow-500 btn-transition relative" data-testid={`ml-package-${pkg.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-semibold text-sm leading-tight">{pkg.name}</p>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <button onClick={() => openEditPkg(pkg)} title="Editar" className="text-slate-500 hover:text-amber-600" data-testid={`ml-edit-${pkg.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => deletePkg(pkg)} title="Eliminar" className="text-slate-500 hover:text-rose-600" data-testid={`ml-delete-${pkg.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <ul className="text-xs text-slate-600 space-y-0.5 mb-3">
                    {(pkg.components || []).map((c, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <span className="font-semibold">{c.qty}×</span>
                        <span>{c.product_label || c.product_id}</span>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" onClick={() => addToCart(pkg)} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white" data-testid={`ml-add-${pkg.id}`}>
                    <Plus size={14} className="mr-1" /> Agregar
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Carrito */}
          <div className="bg-white border border-border rounded-sm flex flex-col" style={{ height: 'fit-content' }}>
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2"><ShoppingCart size={18} /> Salida ({totalPackages} pkts)</h2>
              {cart.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setCart([])} data-testid="ml-clear-cart">
                  <X size={14} /> Limpiar
                </Button>
              )}
            </div>
            <div className="p-3 space-y-2">
              {cart.length === 0 && <p className="text-center text-muted-foreground text-sm py-6">Sin paquetes seleccionados</p>}
              {cart.map((line) => (
                <div key={line.ml_package_id} className="border rounded p-2 flex items-center gap-2" data-testid={`ml-cart-line-${line.ml_package_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{line.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {line.components?.map(c => `${c.qty}× ${(c.product_label || '').slice(0, 18)}`).join(' · ')}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min="1"
                    value={line.package_qty}
                    onChange={(e) => updateCartQty(line.ml_package_id, e.target.value)}
                    className="w-16 text-center"
                    data-testid={`ml-cart-qty-${line.ml_package_id}`}
                  />
                  <button onClick={() => updateCartQty(line.ml_package_id, 0)} className="text-rose-500 hover:text-rose-700" data-testid={`ml-cart-remove-${line.ml_package_id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <>
                <div className="border-t p-3 bg-yellow-50 space-y-2">
                  <p className="text-xs font-semibold text-yellow-900 uppercase tracking-wide">Total mercancía a sacar</p>
                  <ul className="text-sm space-y-0.5" data-testid="ml-totals">
                    {productTotals.map((p) => {
                      const insufficient = p.stock != null && p.total > p.stock;
                      return (
                        <li key={p.product_id} className={`flex justify-between ${insufficient ? 'text-rose-700 font-semibold' : ''}`}>
                          <span className="truncate mr-2">{p.label}</span>
                          <span>
                            <b>{p.total}</b>
                            {p.stock != null && (
                              <span className={`text-[10px] ml-1 ${insufficient ? 'text-rose-600' : 'text-muted-foreground'}`}>
                                / stock {p.stock}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex justify-between border-t pt-2 text-sm font-bold">
                    <span>Total piezas:</span>
                    <span>{totalPieces}</span>
                  </div>
                  {hasInsufficientStock && (
                    <p className="text-xs text-rose-700 font-semibold">⚠️ Stock insuficiente para algún producto</p>
                  )}
                </div>
                <div className="p-3">
                  <Button
                    onClick={() => setSubmitDialog({ open: true, pin: '', notes: '', submitting: false })}
                    disabled={hasInsufficientStock || cart.length === 0}
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                    data-testid="ml-submit-btn"
                  >
                    🔒 Procesar salida (PIN)
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Historial de salidas ML */}
        <div className="bg-white border border-border rounded-sm mt-6">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold">Historial de salidas ({history.length})</h2>
            <p className="text-[11px] text-muted-foreground">Marca ✓ cuando ya hayas cuadrado la salida (pide tu contraseña)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ml-history-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Folio</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Fecha</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Paquetes</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-600">Piezas</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Detalle</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase text-slate-600">Cuadrada</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {history.length === 0 && (
                  <tr><td colSpan="6" className="px-3 py-6 text-center text-muted-foreground">Sin salidas ML registradas</td></tr>
                )}
                {history.map((ex) => {
                  const totalPkgs = (ex.ml_package_breakdown || []).reduce((s, b) => s + (b.package_qty || 0), 0);
                  const fecha = ex.created_at ? new Date(ex.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '';
                  return (
                    <tr key={ex.id} className="hover:bg-slate-50" data-testid={`ml-history-row-${ex.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-xs">{ex.folio}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fecha}</td>
                      <td className="px-3 py-2 text-right">{totalPkgs}</td>
                      <td className="px-3 py-2 text-right">{ex.total_qty}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="space-y-0.5">
                          {(ex.ml_package_breakdown || []).slice(0, 3).map((b, i) => (
                            <div key={i} className="text-slate-600">{b.package_qty}× {b.name}</div>
                          ))}
                          {(ex.ml_package_breakdown || []).length > 3 && (
                            <div className="text-[10px] text-muted-foreground">+ {(ex.ml_package_breakdown || []).length - 3} más…</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {ex.reconciled ? (
                          <div className="inline-flex flex-col items-center" data-testid={`ml-reconciled-${ex.id}`}>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-emerald-500 text-white">
                              ✓ Cuadrada
                            </span>
                            {ex.reconciled_by_name && (
                              <span className="text-[9px] text-muted-foreground mt-0.5">{ex.reconciled_by_name}</span>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setReconcileDialog({ open: true, exit: ex, password: '', submitting: false })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs hover:bg-slate-100 hover:border-emerald-500 hover:text-emerald-700 btn-transition"
                            data-testid={`ml-reconcile-btn-${ex.id}`}
                          >
                            ☐ Marcar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Diálogo de PIN */}
      <Dialog open={submitDialog.open} onOpenChange={(o) => !o && !submitDialog.submitting && setSubmitDialog({ open: false, pin: '', notes: '', submitting: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>🔒 Autorizar salida ML</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {totalPackages} paquete(s) · {totalPieces} pzas
            </p>
            <div>
              <Label>PIN de autorización</Label>
              <Input
                type="password"
                inputMode="numeric"
                value={submitDialog.pin}
                onChange={(e) => setSubmitDialog(s => ({ ...s, pin: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && submitDialog.pin && submitExit()}
                placeholder="••••••"
                autoFocus
                data-testid="ml-pin-input"
              />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input
                value={submitDialog.notes}
                onChange={(e) => setSubmitDialog(s => ({ ...s, notes: e.target.value }))}
                data-testid="ml-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setSubmitDialog({ open: false, pin: '', notes: '', submitting: false })} disabled={submitDialog.submitting}>
                Cancelar
              </Button>
              <Button onClick={submitExit} disabled={!submitDialog.pin || submitDialog.submitting} data-testid="ml-submit-pin">
                {submitDialog.submitting ? 'Procesando…' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de creación/edición de paquete (estilo POS) */}
      <Dialog open={pkgDialog.open} onOpenChange={(o) => !o && setPkgDialog({ open: false, editing: null, name: '', components: [], search: '' })}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{pkgDialog.editing ? 'Editar paquete' : 'Nuevo paquete ML'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Izquierda: buscador + grid de productos */}
            <div className="flex flex-col min-h-0 border rounded-sm">
              <div className="p-2 border-b bg-slate-50">
                <Input
                  value={pkgDialog.search}
                  onChange={(e) => setPkgDialog({ ...pkgDialog, search: e.target.value })}
                  placeholder="Buscar producto…"
                  autoFocus
                  data-testid="ml-pkg-search"
                />
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="ml-pkg-product-list">
                {(() => {
                  const t = (pkgDialog.search || '').toLowerCase().trim();
                  const list = sellableProducts.filter(p =>
                    !t || (p.label || '').toLowerCase().includes(t)
                  );
                  if (list.length === 0) {
                    return <p className="text-center text-muted-foreground text-sm py-6">Sin coincidencias</p>;
                  }
                  return list.slice(0, 100).map((p) => {
                    const inCart = pkgDialog.components.find(c => c.product_id === p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addProductToPkg(p)}
                        className={`w-full text-left border rounded px-2 py-1.5 flex items-center justify-between hover:bg-yellow-50 btn-transition ${inCart ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200'}`}
                        data-testid={`ml-pkg-product-${p.id}`}
                      >
                        <span className="text-sm truncate mr-2">{p.label}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          stock {p.stock}{inCart ? ` · ${inCart.qty}×` : ''}
                        </span>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Derecha: nombre + componentes seleccionados */}
            <div className="flex flex-col min-h-0">
              <div className="mb-2">
                <Label className="text-xs">Nombre del paquete *</Label>
                <Input
                  value={pkgDialog.name}
                  onChange={(e) => setPkgDialog({ ...pkgDialog, name: e.target.value })}
                  placeholder="Ej: Paquete 3 mesas + 12 sillas"
                  data-testid="ml-pkg-name"
                />
              </div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Componentes ({pkgDialog.components.length})</Label>
                {pkgDialog.components.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPkgDialog({ ...pkgDialog, components: [] })}
                    className="text-[11px] text-rose-500 hover:underline"
                  >Limpiar</button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto border rounded-sm p-2 space-y-1 bg-slate-50 min-h-[200px]">
                {pkgDialog.components.length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-8">
                    Haz clic en un producto de la izquierda para agregarlo.
                  </p>
                )}
                {pkgDialog.components.map((c) => (
                  <div key={c.product_id} className="bg-white border rounded px-2 py-1.5 flex items-center gap-2" data-testid={`ml-pkg-comp-${c.product_id}`}>
                    <span className="flex-1 text-sm truncate" title={c.label}>{c.label || c.product_id}</span>
                    <button
                      type="button"
                      onClick={() => setPkgComponentQty(c.product_id, c.qty - 1)}
                      className="w-6 h-6 rounded border hover:bg-slate-100 flex items-center justify-center text-xs"
                    >−</button>
                    <Input
                      type="number"
                      min="1"
                      value={c.qty}
                      onChange={(e) => setPkgComponentQty(c.product_id, e.target.value)}
                      className="w-14 text-center text-sm h-7"
                      data-testid={`ml-pkg-comp-qty-${c.product_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setPkgComponentQty(c.product_id, c.qty + 1)}
                      className="w-6 h-6 rounded border hover:bg-slate-100 flex items-center justify-center text-xs"
                    >+</button>
                    <button
                      type="button"
                      onClick={() => removePkgComponent(c.product_id)}
                      className="text-rose-500 hover:text-rose-700 ml-1"
                      data-testid={`ml-pkg-comp-remove-${c.product_id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
              {pkgDialog.components.length > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
                  <span>Total piezas:</span>
                  <span className="font-semibold">{pkgDialog.components.reduce((s, c) => s + c.qty, 0)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => setPkgDialog({ open: false, editing: null, name: '', components: [], search: '' })}>Cancelar</Button>
            <Button onClick={savePkg} disabled={pkgDialog.components.length === 0 || !pkgDialog.name.trim()} className="bg-yellow-600 hover:bg-yellow-700" data-testid="ml-pkg-save">
              {pkgDialog.editing ? 'Guardar cambios' : 'Crear paquete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Diálogo: marcar cuadrada (pide contraseña admin) */}
      <Dialog open={reconcileDialog.open} onOpenChange={(o) => !o && !reconcileDialog.submitting && setReconcileDialog({ open: false, exit: null, password: '', submitting: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>✓ Marcar salida como cuadrada</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 border rounded p-2 text-sm">
              <div className="font-mono font-semibold">{reconcileDialog.exit?.folio}</div>
              <div className="text-xs text-muted-foreground">{reconcileDialog.exit?.total_qty} pzas</div>
            </div>
            <div>
              <Label>Tu contraseña de admin</Label>
              <Input
                type="password"
                value={reconcileDialog.password}
                onChange={(e) => setReconcileDialog(s => ({ ...s, password: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && reconcileDialog.password && submitReconcile()}
                autoFocus
                data-testid="ml-reconcile-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setReconcileDialog({ open: false, exit: null, password: '', submitting: false })} disabled={reconcileDialog.submitting}>
                Cancelar
              </Button>
              <Button onClick={submitReconcile} disabled={!reconcileDialog.password || reconcileDialog.submitting} data-testid="ml-reconcile-submit" className="bg-emerald-600 hover:bg-emerald-700">
                {reconcileDialog.submitting ? 'Validando…' : 'Confirmar ✓'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default MercadoLibre;
